import { ExpandMoreRounded } from '@mui/icons-material';
import {
  IconButton,
  Popover,
  SxProps,
  Theme,
  Typography,
  styled,
} from '@mui/material';
import { useState } from 'react';
import { Listbox, ListboxProps } from './Listbox';

export interface MultiSelectListboxProps<T>
  extends Omit<ListboxProps<T>, 'options'> {
  label?: string;
  options?: readonly T[];
  sx?: SxProps<Theme>;
  icon?: React.ReactNode;
}

export function MultiSelectListbox<T>({
  label,
  options,
  getOptionKey,
  getOptionLabel,
  selectedKeys,
  onChange,
  onInputTextChange,
  emptyState,
  hideSearch,
  sx,
  icon,
}: MultiSelectListboxProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const loading = !options;

  return (
    <>
      {icon ? (
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          {icon}
        </IconButton>
      ) : (
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={loading}
          sx={sx}
        >
          <Typography variant="body1" noWrap>
            {label}
            {selectedKeys.size !== 0 && ` (${selectedKeys.size})`}
          </Typography>
          <ExpandMoreRounded sx={{ color: 'text.secondary' }} />
        </Button>
      )}

      {!loading && (
        <Popover
          open={!!anchorEl}
          onClose={() => {
            setAnchorEl(null);
            onInputTextChange?.('');
          }}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Listbox
            options={options}
            getOptionKey={getOptionKey}
            getOptionLabel={getOptionLabel}
            selectedKeys={selectedKeys}
            onChange={onChange}
            emptyState={emptyState}
            hideSearch={hideSearch}
            onInputTextChange={onInputTextChange}
          />
        </Popover>
      )}
    </>
  );
}

const Button = styled('button')(({ theme }) => ({
  height: 40,
  width: 180,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 8px 0 16px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
}));
